import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"

import {
  NextArrowIcon,
  PlayIcon,
  PrevArrowIcon,
  ReviewsIcon,
} from "../../icons/shopify-themes"

const formatSlideNumber = number => String(number).padStart(2, "0")

const ShopifyThemesReviews = ({ heading, totalReviews, slides, video }) => {
  const sliderRef = useRef(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)

  useEffect(() => {
    let isMounted = true
    let reviewsCarousel = null

    setCurrentSlide(0)

    if (!sliderRef.current || slides.length === 0) {
      return () => {
        isMounted = false
      }
    }

    import("@glidejs/glide")
      .then(module => {
        if (!isMounted || !sliderRef.current) {
          return
        }

        const Glide = module.default || module
        reviewsCarousel = new Glide(sliderRef.current, {
          rewind: false,
        })

        reviewsCarousel.on("mount.after", () => {
          setCurrentSlide(reviewsCarousel.index)
        })

        reviewsCarousel.on("move.after", () => {
          setCurrentSlide(reviewsCarousel.index)
        })

        reviewsCarousel.mount()
      })
      .catch(error => {
        console.error("Failed to initialize reviews carousel:", error)
      })

    return () => {
      isMounted = false

      if (reviewsCarousel) {
        reviewsCarousel.destroy()
      }
    }
  }, [slides.length])

  const handlePlayVideo = () => {
    setIsVideoPlaying(true)
  }

  const posterClasses = classNames(
    "reviews__video-poster-container",
    {
      "reviews__video-poster-container--hidden": isVideoPlaying,
    }
  )
  const videoClasses = classNames("reviews__video", {
    "is-active": isVideoPlaying,
  })

  return (
    <section className="reviews">
      <div className="reviews__container container container--products-landing">
        <div className="reviews__content">
          <h2 className="reviews__heading">{heading}</h2>

          <p className="reviews__total-reviews">{totalReviews}</p>

          <div
            className="reviews__slider glide"
            ref={sliderRef}
          >
            <div
              className="reviews__container glide__track"
              data-glide-el="track"
            >
              <ul className="glide__slides reviews__slides">
                {slides.map(({ author, date, texts }) => (
                  <li
                    className="glide__slide reviews__slide"
                    key={`${author}-${date}`}
                  >
                    <div className="reviews__slide-header">
                      <div className="reviews__slide-header-icon">
                        <ReviewsIcon />
                      </div>

                      <p className="reviews__slide-header-author">{author}</p>

                      <p className="reviews__slide-header-date">{date}</p>
                    </div>

                    {texts.map((text, index) => (
                      <p className="reviews__slide-text" key={index}>
                        {text}
                      </p>
                    ))}
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="reviews__arrows glide__arrows"
              data-glide-el="controls"
            >
              <button
                className="reviews__arrow reviews__arrow--prev glide__arrow glide__arrow--left"
                type="button"
                data-glide-dir="<"
                aria-label="Go to previous slide"
              >
                <PrevArrowIcon />
              </button>

              <div>
                {formatSlideNumber(currentSlide + 1)}
              </div>

              <div>/</div>

              <div>
                {formatSlideNumber(slides.length)}
              </div>

              <button
                className="reviews__arrow reviews__arrow--next glide__arrow glide__arrow--right"
                type="button"
                data-glide-dir=">"
                aria-label="Go to next slide"
              >
                <NextArrowIcon />
              </button>
            </div>
          </div>
        </div>

        <div className="reviews__video-container">
          <div className={posterClasses} onClick={handlePlayVideo}>
            <div className="reviews__video-poster-image-container">
              <img
                className="reviews__video-poster-image"
                src={video.poster.src}
                srcSet={video.poster.srcSet}
                width={video.poster.width}
                height={video.poster.height}
                loading="lazy"
                decoding="async"
                alt={video.poster.alt}
              />
            </div>

            <button
              className="reviews__video-button"
              type="button"
              aria-label="Play video"
            >
              <PlayIcon />
            </button>
          </div>

          <iframe
            className={videoClasses}
            width={video.width}
            height={video.height}
            src={isVideoPlaying ? video.src : undefined}
            title={video.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </section>
  )
}

ShopifyThemesReviews.propTypes = {
  heading: PropTypes.string.isRequired,
  totalReviews: PropTypes.string.isRequired,
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      author: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      texts: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  video: PropTypes.shape({
    src: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    poster: PropTypes.shape({
      src: PropTypes.string.isRequired,
      srcSet: PropTypes.string,
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
      alt: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
}

export default ShopifyThemesReviews
